// ── BCHNostr — Tip control for a video, gated by creator verification ─
//
// The single place that decides whether a video shows a live tip button or
// a "tips disabled" affordance. The rule (see `videoTipEligibility` in
// lib/creatorVerify.ts):
//
//   · YouTube / Odysee → tips only when the poster PROVED they created the
//     video. An unverified ("shared") video can't be tipped, so nobody farms
//     tips off a video they merely re-posted.
//   · Nostr-native uploads and every other provider → tips allowed.
//
// This keeps the whole policy in one component, so every surface (feed card,
// channel, watch page) behaves identically and any future change is made once.

import React, { useState } from 'react';
import { Coins, BadgeCheck, ShieldAlert } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useVideoTipEligibility } from '../hooks/useCreatorVerification';
import BCHTipButton from './BCHTipButton';
import VerifyChannelModal from './VerifyChannelModal';
import { verifyPlatformForProvider, verifyPlatformLabel } from '../lib/creatorVerify';
import type { BCHVideo } from '../lib/video';

interface VideoTipControlProps {
  video: BCHVideo;
  recipientProfile?: unknown;
  /** Pass the video event id so the tip receipt is coordinate-tagged. */
  withEventId?: boolean;
  dropdownAlign?: 'center' | 'right';
  /** Compact rendering for feed cards (smaller "disabled" pill). */
  compact?: boolean;
  /** Called after the owner successfully verifies, to refresh labels. */
  onVerified?: () => void;
}

export default function VideoTipControl({
  video, recipientProfile, withEventId = false,
  dropdownAlign = 'center', compact = false, onVerified,
}: VideoTipControlProps) {
  const { user } = useAppContext();
  const { eligibility } = useVideoTipEligibility(video);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const isOwner = !!user && user.pubkey === video.pubkey;
  const platform = verifyPlatformForProvider(video.provider);
  const platformLabel = platform ? verifyPlatformLabel(platform) : '';

  // ── Eligible → the real tip button ──
  if (eligibility === 'eligible') {
    return (
      <BCHTipButton
        recipientPubkey={video.pubkey}
        recipientProfile={recipientProfile}
        eventId={withEventId ? video.id : undefined}
        dropdownAlign={dropdownAlign}
      />
    );
  }

  // ── Checking → neutral placeholder while verification resolves ──
  if (eligibility === 'checking') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground/70 font-semibold ${
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
        }`}
        title="Checking whether the poster verified this channel…"
      >
        <Coins className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> Tip
      </span>
    );
  }

  // ── Unverified → tips disabled; owner gets a shortcut to verify ──
  // Owner: an actionable prompt to verify and unlock tips.
  if (isOwner) {
    return (
      <>
        <button
          onClick={() => setVerifyOpen(true)}
          className={`no-navigate inline-flex items-center gap-1 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 transition-colors ${
            compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
          }`}
          title={`Verify you control this ${platformLabel || ''} channel to receive tips on this video`}
        >
          <BadgeCheck className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
          {compact ? 'Verify' : 'Verify to earn tips'}
        </button>
        {verifyOpen && (
          <VerifyChannelModal
            onClose={() => setVerifyOpen(false)}
            onVerified={() => { setVerifyOpen(false); onVerified?.(); }}
          />
        )}
      </>
    );
  }

  // Viewer: a muted, non-actionable "tips disabled" tag.
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground/70 font-medium cursor-default ${
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
      }`}
      title={`Tips are disabled: the poster hasn't verified they created this ${platformLabel || 'video'}. Only a verified creator can receive tips on a video.`}
    >
      <ShieldAlert className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {compact ? 'No tips' : 'Tips disabled'}
    </span>
  );
}
